import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  ConfigResponse,
  Order,
  OnsiteVariant,
  PickUnit,
  ScenarioName,
  ShippingAddress,
} from "../shared/domain";
import { matchBarcode, type BarcodeMatch } from "../shared/domain";
import { api, ApiError } from "../api/client";
import { Dialog } from "./Dialog";
import { OnsiteFlow } from "./Onsite";
import { CloseablePanel } from "./Panel";
import { useKeyboardScanner } from "./useKeyboardScanner";
import { AuthGate } from "./Auth";
import { swipeOrderIndex } from "./orderSwipe";

type Screen = "orders" | "picking" | "delivery" | "onsite";
type Theme = "light" | "dark";

interface ScanFeedback {
  kind: "success" | "error" | "warning";
  title: string;
  message: string;
  scanned?: string;
  expected?: string;
  context?: string;
}

interface PendingConfirmation {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
}

interface WrongScanToast {
  scanned: string;
  expected: PickUnit["variant"] | null;
  variants: OnsiteVariant[];
  status: "loading" | "found" | "ambiguous" | "not-found" | "unavailable";
}

interface DeliveryState {
  fulfillmentOperationKey: string;
  fulfillmentStatus: "idle" | "pending";
  fulfillmentError: ApiError | null;
  fulfillmentUnknown: boolean;
  fulfillmentReconciliationMessage: string;
}

function newOperationKey(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatAge(createdAt: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function formatMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function formatFinancialStatus(value: string): string {
  const labels: Record<string, string> = {
    PAID: "Paid",
    PENDING: "Payment pending",
    AUTHORIZED: "Authorized",
    PARTIALLY_PAID: "Partially paid",
    REFUNDED: "Refunded",
    PARTIALLY_REFUNDED: "Partially refunded",
    VOIDED: "Voided",
    UNPAID: "Unpaid",
  };
  return labels[value] ?? (value.toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase()) || "Payment unknown");
}

function financialStatusClass(value: string): "status-success" | "status-warning" | "status-neutral" {
  if (value === "PAID") return "status-success";
  if (["PENDING", "PARTIALLY_PAID", "REFUNDED", "PARTIALLY_REFUNDED", "VOIDED", "UNPAID"].includes(value)) return "status-warning";
  return "status-neutral";
}

function useInitialTheme(): Theme {
  let stored: string | null = null;
  try { stored = typeof window !== "undefined" ? window.localStorage.getItem("natalify-theme") : null; } catch { /* Theme storage is optional. */ }
  if (stored === "light" || stored === "dark") return stored;
  return "light";
}

function ProductImage({ variant, alt, className = "" }: { variant: Pick<PickUnit["variant"], "variantId" | "imageUrl" | "productImageUrl">; alt: string; className?: string }) {
  const sources = useMemo(() => [variant.imageUrl, variant.productImageUrl].filter((value): value is string => Boolean(value)), [variant.imageUrl, variant.productImageUrl]);
  const [sourceIndex, setSourceIndex] = useState(0);
  useEffect(() => setSourceIndex(0), [variant.variantId]);
  const source = sources[sourceIndex];
  return (
    <div className={`product-image ${className}`}>
      {source ? (
        <img src={source} alt={alt} onError={() => setSourceIndex((index) => index + 1)} />
      ) : (
        <div className="image-placeholder" aria-label={`${alt}; image unavailable`} role="img">
          <span aria-hidden="true">✦</span>
          <small>Image unavailable</small>
        </div>
      )}
    </div>
  );
}

function FeedbackMessage({ tone, icon, title, message, details = [] }: { tone: "error" | "warning"; icon: string; title: string; message: string; details?: ReactNode[] }) {
  return (
    <CloseablePanel className={`feedback feedback-${tone}`} role={tone === "error" ? "alert" : "status"} resetKey={`${title}|${message}|${details.length}`} closeLabel={`Close ${title}`}>
      <span className="feedback-icon" aria-hidden="true">{icon}</span>
      <div><strong>{title}</strong><p>{message}</p>{details.map((detail, index) => <p className="feedback-detail" key={index}>{detail}</p>)}</div>
    </CloseablePanel>
  );
}

function NoticeMessage({ title, message }: { title: string; message: string }) {
  return (
    <CloseablePanel className="notice notice-info" role="status" resetKey={`${title}|${message}`} closeLabel={`Close ${title}`}>
      <span className="notice-icon" aria-hidden="true">i</span>
      <div><strong>{title}</strong><p>{message}</p></div>
    </CloseablePanel>
  );
}

function AppHeader({ screen, theme, mode, refreshing, onRefresh, onToggleTheme, onNavigate }: { screen: Screen; theme: Theme; mode: ConfigResponse["mode"]; refreshing: boolean; onRefresh: () => void; onToggleTheme: () => void; onNavigate: (screen: Screen) => void }) {
  const steps: Array<{ id: Screen; label: string; number: string }> = [
    { id: "orders", label: "Orders", number: "1" },
    { id: "picking", label: "Pick", number: "2" },
    { id: "delivery", label: "Complete", number: "3" },
  ];
  const currentStepIndex = steps.findIndex((step) => step.id === screen);
  return (
    <header className="app-header">
      <div className="brand-row">
        <div className="app-brand">
          <div>
            <p className="brand-mark">NATALIFY</p>
            <p className="brand-subtitle">packing desk</p>
          </div>
          <img className="saysky-banner" src="/brand/banner.png" alt="SaySky logo" />
        </div>
        <div className="header-actions">
          <span className="mode-badge"><span className="status-dot" /> {mode === "mock" ? "Demo" : "Shopify"}</span>
          {screen === "orders" && <button className="button button-secondary header-refresh" type="button" onClick={onRefresh} disabled={refreshing} aria-label="Refresh orders">
            <span aria-hidden="true">↻</span> {refreshing ? "Refreshing…" : "Refresh"}
          </button>}
          <button className="icon-button theme-toggle" type="button" onClick={onToggleTheme} aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}>
            {theme === "light" ? "☾" : "☀"}
          </button>
        </div>
      </div>
      <nav className="stepper" aria-label="Packing steps">
        {steps.map((step, index) => (
          <div className={`step ${step.id === screen ? "step-current" : ""} ${currentStepIndex > index ? "step-done" : ""}`} key={step.id} aria-current={step.id === screen ? "step" : undefined}>
            {currentStepIndex > index ? <button className="step-button" type="button" onClick={() => onNavigate(step.id)} aria-label={`Back to ${step.label}`}><span className="step-number">✓</span><span>{step.label}</span></button> : <><span className="step-number">{step.number}</span><span>{step.label}</span></>}
            {index < steps.length - 1 && <span className="step-line" aria-hidden="true" />}
          </div>
        ))}
      </nav>
    </header>
  );
}

function MockTools({ config, busy, onScenarioChange, onReset }: { config: ConfigResponse; busy: boolean; onScenarioChange: (scenario: ScenarioName) => void; onReset: () => void }) {
  return (
    <aside className="mock-tools card" aria-label="Mock scenario tools">
      <details>
        <summary><span>Scenario</span><span className="summary-hint">test data</span></summary>
        <div className="mock-tools-body">
          <div className="field-row mock-controls">
            <label htmlFor="mock-scenario">Scenario</label>
            <select id="mock-scenario" value={config.scenario} disabled={busy} onChange={(event) => onScenarioChange(event.target.value as ScenarioName)}>
              {config.scenarios.map((scenario) => <option value={scenario.id} key={scenario.id}>{scenario.label}</option>)}
            </select>
            <button className="button button-secondary" type="button" disabled={busy} onClick={onReset}>{busy ? "Resetting…" : "Reset"}</button>
          </div>
          <p className="scenario-description"><strong>{config.scenarios.find((scenario) => scenario.id === config.scenario)?.label}</strong></p>
        </div>
      </details>
    </aside>
  );
}

function AttentionBanner({ count, statuses, adminUrl }: { count: number; statuses: string[]; adminUrl: string }) {
  if (!count) return null;
  return (
    <CloseablePanel className="notice notice-warning" role="status" resetKey={`${count}|${statuses.join("|")}`} closeLabel="Close attention notice">
      <span className="notice-icon" aria-hidden="true">!</span>
      <div>
        <strong>{count} order{count === 1 ? "" : "s"} need attention</strong>
        <p>{statuses.join(" · ")} · <a href={adminUrl} target="_blank" rel="noreferrer">Review ↗</a></p>
      </div>
    </CloseablePanel>
  );
}

function OrdersScreen({
  orders,
  currentIndex,
  loading,
  loadError,
  stale,
  attention,
  adminUrl,
  onRefresh,
  onPrevious,
  onNext,
  onView,
  onOnsiteSale,
}: {
  orders: Order[];
  currentIndex: number;
  loading: boolean;
  loadError: ApiError | null;
  stale: boolean;
  attention: { count: number; statuses: string[] };
  adminUrl: string;
  onRefresh: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onView: () => void;
  onOnsiteSale: () => void;
}) {
  const order = orders[currentIndex] ?? null;
  const [preview, setPreview] = useState<PickUnit | null>(null);
  const swipeStart = useRef<{ x: number; y: number; id: number } | null>(null);
  const swiped = useRef(false);
  useEffect(() => { setPreview(null); swipeStart.current = null; }, [order?.id]);
  return (
    <main className="screen-shell orders-screen" aria-label="Orders">
      <div className="orders-action-row"><div><p className="eyebrow">Physical sale</p><strong>Record a walk-in purchase</strong></div><button className="button onsite-entry-button" type="button" onClick={onOnsiteSale} title="Start a new on-site sale"><span aria-hidden="true">▣</span> On-site Sale</button></div>
      <AttentionBanner count={attention.count} statuses={attention.statuses} adminUrl={adminUrl} />

      {stale && (
        <CloseablePanel className="notice notice-danger" role="alert" resetKey="orders-stale" closeLabel="Close refresh notice">
          <span className="notice-icon" aria-hidden="true">!</span>
          <div><strong>Refresh needed</strong><p>Orders are not current. Refresh before opening one.</p></div>
        </CloseablePanel>
      )}

      {loading && orders.length === 0 ? (
        <section className="card loading-card" aria-live="polite"><div className="spinner" /><h2>Loading orders…</h2></section>
      ) : loadError && orders.length === 0 ? (
        <section className="card empty-card" role="alert"><div className="empty-icon">!</div><h2>Orders unavailable</h2><p>{loadError.message}</p><button className="button button-primary" type="button" onClick={onRefresh}>Retry</button></section>
      ) : !order ? (
        <section className="card empty-card"><div className="empty-icon">✓</div><p className="eyebrow">Queue clear</p><h2>No orders</h2><button className="button button-secondary" type="button" onClick={onRefresh} disabled={loading}>Check again</button></section>
      ) : (
        <section className="order-stage" aria-label="Current order">
          <div className="carousel-meta"><span>Swipe left / right to browse</span><strong aria-live="polite">{currentIndex + 1} / {orders.length}</strong></div>
          <article className="card order-card" onPointerDown={(event) => {
            swiped.current = false;
            if (event.pointerType !== "touch" || !event.isPrimary || preview) { swipeStart.current = null; return; }
            swipeStart.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
          }} onPointerUp={(event) => {
            const start = swipeStart.current;
            swipeStart.current = null;
            if (!start || start.id !== event.pointerId) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            const nextIndex = swipeOrderIndex(dx, dy, currentIndex, orders.length);
            swiped.current = Math.abs(dx) >= 60 && Math.abs(dx) >= Math.abs(dy) * 1.5;
            if (nextIndex > currentIndex) onNext();
            if (nextIndex < currentIndex) onPrevious();
          }} onPointerCancel={() => { swipeStart.current = null; }} onClickCapture={(event) => {
            if (swiped.current) { event.preventDefault(); event.stopPropagation(); swiped.current = false; }
          }}>
            <div className="order-card-top">
              <div><span className="order-number">{order.orderNumber}</span><span className="order-age">{formatAge(order.createdAt)}</span></div>
              <div className="order-statuses">
                <span className="status-pill status-neutral">Unfulfilled</span>
                <span className={`status-pill ${financialStatusClass(order.financialStatus)}`}>{formatFinancialStatus(order.financialStatus)}</span>
                {order.units.some((unit) => !unit.variant.barcode) && <span className="status-pill status-warning">Barcode missing</span>}
                {order.units.some((unit) => !unit.variant.imageUrl && !unit.variant.productImageUrl) && <span className="status-pill status-warning">Image missing</span>}
              </div>
            </div>
            <div className="order-info">
              <div className="order-customer"><h2>{order.customerName}</h2><p className="muted">City: {order.shippingAddress.city || "Not set"} · {formatMoney(order.total, order.currency)}</p></div>
              <div className="order-note"><span className="label">Note</span><p dir="auto">{order.note || "—"}</p></div>
            </div>
            <div className="order-items">
              <div className="order-items-heading"><span className="label">Items</span><strong>{order.lines.length} product{order.lines.length === 1 ? "" : "s"} · {order.units.length} unit{order.units.length === 1 ? "" : "s"}</strong></div>
              <div className="thumbnail-row" aria-label={`${order.units.length} item units`}>
                {order.units.map((unit) => <button className="order-item-preview" type="button" key={unit.unitId} onClick={() => setPreview(unit)} aria-label={`View ${unit.variant.productName}, SKU ${unit.variant.sku || "unavailable"}`} aria-haspopup="dialog"><ProductImage variant={unit.variant} alt="" className="thumbnail" /><span>{unit.variant.size || "View"}</span></button>)}
              </div>
            </div>
            <div className="order-card-footer"><button className="button button-primary" type="button" onClick={onView} disabled={stale}>View order <span aria-hidden="true">→</span></button></div>
          </article>
          <div className="carousel-controls">
            <button className="button button-secondary" type="button" onClick={onPrevious} disabled={currentIndex <= 0} aria-label="Previous order">← Previous</button>
            <button className="button button-secondary" type="button" onClick={onNext} disabled={currentIndex >= orders.length - 1} aria-label="Next order">Next →</button>
          </div>
          {loadError && orders.length > 0 && <p className="inline-error" role="alert">Refresh failed. Retry before opening an order.</p>}
        </section>
      )}
      {preview && <Dialog title={preview.variant.productName} eyebrow="Product details" onClose={() => setPreview(null)}>
        <ProductImage variant={preview.variant} alt={preview.variant.productName} className="preview-product-image" />
        <dl className="product-detail-list"><div><dt>SKU</dt><dd><code>{preview.variant.sku || "Unavailable"}</code></dd></div>{Object.entries(preview.variant.options).map(([name, value]) => <div key={name}><dt>{name}</dt><dd dir="auto">{value}</dd></div>)}<div><dt>Barcode</dt><dd><code>{preview.variant.barcode || "Unavailable"}</code></dd></div></dl>
        <button className="button button-primary button-full" type="button" onClick={() => setPreview(null)}>Back to order</button>
      </Dialog>}
    </main>
  );
}

function ItemStrip({ units, confirmedIds, currentUnitId, onSelect, onUndo }: { units: PickUnit[]; confirmedIds: ReadonlySet<string>; currentUnitId: string | null; onSelect: (unitId: string) => void; onUndo: (unitId: string) => void }) {
  const visibleUnits = units;
  return (
    <div className="item-strip" role="list" aria-label="Physical item progress">
      {visibleUnits.map((unit) => {
        const index = units.findIndex((candidate) => candidate.unitId === unit.unitId);
        const confirmed = confirmedIds.has(unit.unitId);
        const current = currentUnitId === unit.unitId;
        return (
          <div className={`item-chip-wrap ${confirmed ? "item-chip-confirmed" : ""} ${current ? "item-chip-current" : ""}`} role="listitem" key={unit.unitId}>
            <button className="item-chip" type="button" onClick={() => onSelect(unit.unitId)} aria-label={`Item ${index + 1}, ${confirmed ? "confirmed" : "remaining"}, ${unit.variant.productName} ${unit.variant.color ?? ""} ${unit.variant.size ?? ""}`}>
              <ProductImage variant={unit.variant} alt="" className="chip-image" />
              <span className="chip-index">{confirmed ? "✓" : index + 1}</span>
              <span className="chip-status">{confirmed ? "Added" : current ? "Pick now" : "Remaining"}</span>
            </button>
            {confirmed && <button className="chip-undo" type="button" onClick={() => onUndo(unit.unitId)} aria-label={`Undo item ${index + 1}`}>↶</button>}
          </div>
        );
      })}
    </div>
  );
}

function PickingScreen({
  order,
  confirmedIds,
  currentUnit,
  demoScanEnabled,
  feedback,
  wrongScanToast,
  ambiguousCandidates,
  inputBlocked,
  onDemoScan,
  onScan,
  onSelect,
  onUndo,
  onManualRequest,
  onResolveAmbiguous,
  onCloseAmbiguous,
  onDismissWrongScan,
}: {
  order: Order;
  confirmedIds: ReadonlySet<string>;
  currentUnit: PickUnit | null;
  demoScanEnabled: boolean;
  feedback: ScanFeedback | null;
  wrongScanToast: WrongScanToast | null;
  ambiguousCandidates: PickUnit[] | null;
  inputBlocked: boolean;
  onDemoScan: () => void;
  onScan: (value: string) => void;
  onSelect: (unitId: string) => void;
  onUndo: (unitId: string) => void;
  onManualRequest: () => void;
  onResolveAmbiguous: (unitId: string) => void;
  onCloseAmbiguous: () => void;
  onDismissWrongScan: () => void;
}) {
  const demoHoldTimer = useRef<number | undefined>(undefined);
  const clearDemoHold = () => {
    if (demoHoldTimer.current !== undefined) window.clearTimeout(demoHoldTimer.current);
    demoHoldTimer.current = undefined;
  };
  const startDemoHold = () => {
    clearDemoHold();
    if (!currentUnit?.variant.barcode) return;
    demoHoldTimer.current = window.setTimeout(() => {
      demoHoldTimer.current = undefined;
      onDemoScan();
    }, 650);
  };
  useEffect(() => () => clearDemoHold(), []);
  useEffect(() => {
    clearDemoHold();
  }, [currentUnit?.unitId]);
  const scannerInputRef = useKeyboardScanner({ enabled: true, blocked: inputBlocked || Boolean(ambiguousCandidates) || Boolean(wrongScanToast), onScan });
  useEffect(() => {
    if (inputBlocked || ambiguousCandidates || wrongScanToast) return;
    const frame = window.requestAnimationFrame(() => scannerInputRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [ambiguousCandidates, currentUnit?.unitId, inputBlocked, scannerInputRef, wrongScanToast]);
  const extraOptions = currentUnit
    ? Object.entries(currentUnit.variant.options).filter(([name]) => !["color", "size"].includes(name.toLowerCase()))
    : [];
  const feedbackDetails = feedback
    ? [
        feedback.scanned ? <><b>Scanned:</b> <code>{feedback.scanned}</code></> : null,
        feedback.expected ? <><b>Expected:</b> {feedback.expected}</> : null,
        feedback.context ?? null,
      ].filter((detail) => detail !== null)
    : [];
  return (
    <main className="screen-shell picking-screen" aria-label="Pick items">
      <section className="card progress-card">
        <ItemStrip units={order.units} confirmedIds={confirmedIds} currentUnitId={currentUnit?.unitId ?? null} onSelect={onSelect} onUndo={onUndo} />
      </section>

      {feedback && feedback.kind !== "success" && (
        <FeedbackMessage tone={feedback.kind} icon={feedback.kind === "warning" ? "!" : "×"} title={feedback.title} message={feedback.message} details={feedbackDetails} />
      )}

      {currentUnit ? (
        <section className="pick-layout">
          <div className="card current-item-card">
            <ProductImage variant={currentUnit.variant} alt={`${currentUnit.variant.productName}, ${currentUnit.variant.color ?? ""} ${currentUnit.variant.size ?? ""}`} className="hero-product-image" />
            <div className="product-copy">
              <div className="product-kicker"><span className="product-handle-kicker" dir="auto">{currentUnit.variant.productHandle || "Handle unavailable"}</span><span>{currentUnit.instanceNumber} of {order.units.filter((unit) => unit.lineId === currentUnit.lineId).length}</span></div>
              <h2 dir="auto">{currentUnit.variant.productName}</h2>
              {(currentUnit.variant.color || currentUnit.variant.size) && <div className="variant-emphasis">{currentUnit.variant.color && <span>{currentUnit.variant.color}</span>}{currentUnit.variant.color && currentUnit.variant.size && <span className="variant-divider">·</span>}{currentUnit.variant.size && <span>{currentUnit.variant.size}</span>}</div>}
              <div className="product-meta"><div className="product-meta-sku"><code>{currentUnit.variant.sku || "—"}</code></div></div>
              {extraOptions.length > 0 && <div className="extra-options">{extraOptions.map(([name, value]) => <span key={name}><b>{name}:</b> {value}</span>)}</div>}
            </div>
          </div>
          <div className="card scan-card" onPointerDown={(event) => { if (!(event.target as HTMLElement).closest("button")) scannerInputRef.current?.focus({ preventScroll: true }); }}>
            <input ref={scannerInputRef} className="scanner-capture-input" inputMode="none" autoComplete="off" autoCorrect="off" spellCheck={false} tabIndex={-1} aria-label="Barcode scanner input" />
            <div className="scan-card-heading"><div><h2>Scan barcode</h2><code className="scan-target-code">{currentUnit.variant.barcode ?? "No barcode — use Manual"}</code></div><div className="scan-badges">
              {demoScanEnabled && <button className="listen-badge" type="button" disabled={!currentUnit.variant.barcode} title="Hold to demo scan" aria-label={currentUnit.variant.barcode ? "Reader; hold to demo scan" : "Reader; barcode unavailable"} onPointerDown={startDemoHold} onPointerUp={clearDemoHold} onPointerLeave={clearDemoHold} onPointerCancel={clearDemoHold} onContextMenu={(event) => event.preventDefault()} onClick={(event) => { if (event.detail === 0) onDemoScan(); }}><span className="pulse-dot" /> Reader</button>}
              <button className="manual-badge" type="button" onClick={onManualRequest}>Manual</button>
            </div></div>
          </div>
        </section>
      ) : null}

      {wrongScanToast && <Dialog title="Wrong barcode — check the item" eyebrow="Barcode mismatch" description="This scan did not add an item. Compare the details, then scan again." onClose={onDismissWrongScan}>
        <p className="mismatch-barcode">Scanned barcode: <code>{wrongScanToast.scanned}</code></p>
        <div className="scan-comparison" aria-live="polite">
          <section className="comparison-card comparison-scanned"><h3>Scanned item</h3>
            {wrongScanToast.status === "loading" && <p>Looking up this barcode…</p>}
            {wrongScanToast.status === "not-found" && <p>No product found for this barcode.</p>}
            {wrongScanToast.status === "unavailable" && <p>Product lookup unavailable. Check the barcode and try again.</p>}
            {wrongScanToast.status === "ambiguous" && <p>Multiple products share this barcode:</p>}
            {wrongScanToast.variants.map((variant) => <div key={variant.variantId}><ProductImage variant={variant} alt={variant.name} className="comparison-image" /><h4 dir="auto">{variant.name}</h4><dl className="product-detail-list"><div><dt>SKU</dt><dd><code>{variant.sku || "Unavailable"}</code></dd></div>{Object.entries(variant.options).map(([name, value]) => <div className={wrongScanToast.expected?.options[name] !== value ? "detail-different" : ""} key={name}><dt>{name}</dt><dd dir="auto">{value}</dd></div>)}</dl></div>)}
          </section>
          <section className="comparison-card comparison-expected"><h3>Expected item</h3>{wrongScanToast.expected ? <><ProductImage variant={wrongScanToast.expected} alt={wrongScanToast.expected.productName} className="comparison-image" /><h4 dir="auto">{wrongScanToast.expected.productName}</h4><dl className="product-detail-list"><div><dt>SKU</dt><dd><code>{wrongScanToast.expected.sku || "Unavailable"}</code></dd></div>{Object.entries(wrongScanToast.expected.options).map(([name, value]) => <div className={wrongScanToast.variants.length === 1 && wrongScanToast.variants[0].options[name] !== value ? "detail-different" : ""} key={name}><dt>{name}</dt><dd dir="auto">{value}</dd></div>)}<div><dt>Barcode</dt><dd><code>{wrongScanToast.expected.barcode || "Unavailable"}</code></dd></div></dl></> : <p>No remaining item.</p>}</section>
        </div>
        <button className="button button-primary button-full" type="button" onClick={onDismissWrongScan}>Got it — scan again</button>
      </Dialog>}
      {ambiguousCandidates && (
        <Dialog title="Which variant did you scan?" description="This barcode is shared by distinct variants. Choose deliberately; Natalify will not guess." onClose={onCloseAmbiguous}>
          <p className="scan-ambiguous-code">Scanned code: <code>{ambiguousCandidates[0]?.variant.barcode}</code></p>
          <div className="resolution-list">{ambiguousCandidates.map((unit) => <button className="resolution-option" type="button" key={unit.unitId} onClick={() => onResolveAmbiguous(unit.unitId)}><ProductImage variant={unit.variant} alt="" className="resolution-image" /><span><strong>{unit.variant.productName}</strong><small>{unit.variant.color ?? "Color unavailable"} · {unit.variant.size ?? "Size unavailable"}<br /><code>{unit.variant.sku}</code>{unit.variant.productHandle && <><br /><code>{unit.variant.productHandle}</code></>}</small></span><span aria-hidden="true">→</span></button>)}</div>
          <button className="button button-quiet button-full" type="button" onClick={onCloseAmbiguous}>Cancel</button>
        </Dialog>
      )}
    </main>
  );
}

function AddressCard({ address }: { address: ShippingAddress }) {
  return (
    <div className="address-card">
      <div className="address-recipient-row"><div className="address-recipient" dir="auto">{address.recipientName || "Recipient not set"}</div><span className="status-pill status-neutral">{address.country || "—"}</span></div>
      <dl className="address-grid">
        <div><dt>Phone</dt><dd dir="auto">{address.phone || "—"}</dd></div>
        <div><dt>Postal code</dt><dd dir="auto">{address.postalCode || "—"}</dd></div>
        <div><dt>City</dt><dd dir="auto">{address.city || "—"}</dd></div>
        <div><dt>Street / building</dt><dd dir="auto">{[address.street, address.building].filter(Boolean).join(" ") || "—"}</dd></div>
        <div><dt>Apartment / unit</dt><dd dir="auto">{address.unit || "—"}</dd></div>
      </dl>
      <details className="address-more">
        <summary>Full address and note</summary>
        <div className="raw-address"><span className="label">Raw lines</span>{address.addressLines.length ? address.addressLines.map((line, index) => <div dir="auto" key={`${line}-${index}`}>{line}</div>) : <div className="missing-data">No address lines</div>}</div>
        <div className="delivery-note"><span className="label">Note</span><p dir="auto">{address.deliveryNote || "—"}</p></div>
      </details>
    </div>
  );
}

function EditableAddress({ address }: { address: ShippingAddress }) {
  const [editing, setEditing] = useState(false);
  const [savedAddress, setSavedAddress] = useState(address);
  const [draftAddress, setDraftAddress] = useState(address);

  useEffect(() => {
    setEditing(false);
    setSavedAddress(address);
    setDraftAddress(address);
  }, [address]);

  const updateDraft = (field: keyof ShippingAddress, value: string) => {
    setDraftAddress((current) => ({ ...current, [field]: value }));
  };

  const toggleEditing = () => {
    if (editing) {
      const addressLines = [
        [draftAddress.street, draftAddress.building].filter(Boolean).join(" "),
        draftAddress.unit ? `Unit ${draftAddress.unit}` : "",
        [draftAddress.city, draftAddress.postalCode].filter(Boolean).join(" "),
        draftAddress.country,
      ].filter(Boolean);
      const confirmedAddress = { ...draftAddress, addressLines };
      setSavedAddress(confirmedAddress);
      setDraftAddress(confirmedAddress);
    } else {
      setDraftAddress(savedAddress);
    }
    setEditing((current) => !current);
  };

  return (
    <div className={`completion-address ${editing ? "completion-address-editing" : ""}`}>
      <div className="completion-address-heading">
        <h3>Shipping address</h3>
        <button className="button button-secondary address-edit-button" type="button" onClick={toggleEditing}>
          {editing ? "Confirm" : "Edit"}
        </button>
      </div>
      {editing ? (
        <div className="address-editor" aria-label="Edit shipping address">
          <div className="address-editor-fields">
            <div className="form-grid">
              <div className="field field-wide"><label htmlFor="address-recipient">Name</label><input id="address-recipient" value={draftAddress.recipientName} onChange={(event) => updateDraft("recipientName", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-phone">Phone</label><input id="address-phone" inputMode="tel" value={draftAddress.phone} onChange={(event) => updateDraft("phone", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-postal">Postal code</label><input id="address-postal" value={draftAddress.postalCode} onChange={(event) => updateDraft("postalCode", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-city">City</label><input id="address-city" value={draftAddress.city} onChange={(event) => updateDraft("city", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-country">Country</label><input id="address-country" value={draftAddress.country} onChange={(event) => updateDraft("country", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-street">Street</label><input id="address-street" value={draftAddress.street} onChange={(event) => updateDraft("street", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-building">Building</label><input id="address-building" value={draftAddress.building} onChange={(event) => updateDraft("building", event.target.value)} /></div>
              <div className="field"><label htmlFor="address-unit">Apartment / unit</label><input id="address-unit" value={draftAddress.unit} onChange={(event) => updateDraft("unit", event.target.value)} /></div>
              <div className="field field-wide"><label htmlFor="address-note">Delivery note</label><textarea id="address-note" value={draftAddress.deliveryNote} onChange={(event) => updateDraft("deliveryNote", event.target.value)} /></div>
            </div>
          </div>
        </div>
      ) : <AddressCard address={savedAddress} />}
    </div>
  );
}

function DeliveryScreen({
  order,
  confirmedCount,
  delivery,
  onFulfill,
  onReconcileFulfillment,
  readOnly,
}: {
  order: Order;
  confirmedCount: number;
  delivery: DeliveryState;
  onFulfill: () => void;
  onReconcileFulfillment: () => void;
  readOnly: boolean;
}) {
  const canComplete = confirmedCount === order.units.length && !delivery.fulfillmentUnknown && !readOnly;
  return (
    <main className="screen-shell delivery-screen" aria-label="Complete order">
      {readOnly && <NoticeMessage title="Shopify fulfillment is not enabled" message="Grant a fulfillment-order write scope, then enable the server release flag. Packing remains available without changing Shopify." />}
      {delivery.fulfillmentError && <FeedbackMessage tone="error" icon="×" title="Could not complete order" message={delivery.fulfillmentError.message} />}
      {delivery.fulfillmentReconciliationMessage && <NoticeMessage title="Fulfillment reconciliation" message={delivery.fulfillmentReconciliationMessage} />}
      <section className="delivery-flow completion-flow" aria-label="Shopify completion">
        <div className="completion-order-summary">
          <div><p className="eyebrow">Packed order</p><h2>{order.orderNumber}</h2><p>{order.customerName}</p></div>
          <span className="status-pill status-success">✓ {confirmedCount} of {order.units.length} packed</span>
        </div>
        <EditableAddress key={order.id} address={order.shippingAddress} />
        <div className={`shopify-completion ${canComplete ? "shopify-completion-ready" : ""}`}><div><p className="eyebrow">Shopify</p><strong>{canComplete ? "Ready to fulfill" : delivery.fulfillmentUnknown ? "Confirmation needed" : readOnly ? "Writes disabled" : "Packing incomplete"}</strong><p>{delivery.fulfillmentUnknown ? "Shopify may already have completed this order. Check before retrying." : canComplete ? "This marks every packed item fulfilled in Shopify." : readOnly ? "No Shopify changes can be made until the release flag is enabled." : "Confirm every item first."}</p></div><button className="button button-primary button-large" type="button" onClick={onFulfill} disabled={!canComplete || delivery.fulfillmentStatus === "pending"}>{delivery.fulfillmentStatus === "pending" ? "Updating Shopify…" : "Order Fulfilled"}</button></div>
        {delivery.fulfillmentUnknown && <div className="fulfillment-reconcile"><div><p className="eyebrow">Shopify</p><h3>Check fulfillment</h3></div><button className="button button-primary button-wide" type="button" onClick={onReconcileFulfillment} disabled={delivery.fulfillmentStatus === "pending"}>{delivery.fulfillmentStatus === "pending" ? "Checking…" : "Check Shopify"}</button></div>}
      </section>
    </main>
  );
}

export default function App() {
  return <AuthGate><NatalifyApp /></AuthGate>;
}

function NatalifyApp() {
  const [theme, setThemePreference] = useState<Theme>(useInitialTheme);
  const [screen, setScreen] = useState<Screen>("orders");
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [configError, setConfigError] = useState<ApiError | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attention, setAttention] = useState({ count: 0, statuses: [] as string[] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [stale, setStale] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [confirmedIds, setConfirmedIds] = useState<Set<string>>(new Set());
  const [currentUnitId, setCurrentUnitId] = useState<string | null>(null);
  const [scanFeedback, setScanFeedback] = useState<ScanFeedback | null>(null);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [ambiguousCandidates, setAmbiguousCandidates] = useState<PickUnit[] | null>(null);
  const [delivery, setDelivery] = useState<DeliveryState | null>(null);
  const [mockBusy, setMockBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const [wrongScanToast, setWrongScanToast] = useState<WrongScanToast | null>(null);
  const wrongScanRequest = useRef(0);
  const refreshToken = useRef(0);
  const currentOrderIdRef = useRef<string | null>(null);
  const currentIndexRef = useRef(0);
  const ordersRef = useRef<Order[]>([]);
  const screenRef = useRef<Screen>("orders");
  currentOrderIdRef.current = activeOrderId ?? orders[currentIndex]?.id ?? null;
  currentIndexRef.current = currentIndex;
  ordersRef.current = orders;
  screenRef.current = screen;

  const selectedOrder = orders.find((order) => order.id === activeOrderId) ?? orders[currentIndex] ?? null;
  const currentUnit = selectedOrder?.units.find((unit) => unit.unitId === currentUnitId && !confirmedIds.has(unit.unitId)) ?? selectedOrder?.units.find((unit) => !confirmedIds.has(unit.unitId)) ?? null;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("natalify-theme", theme); } catch { /* Theme preference is optional. */ }
  }, [theme]);

  const loadConfig = useCallback(async () => {
    try {
      const nextConfig = await api.getConfig();
      setConfig(nextConfig);
      setConfigError(null);
    } catch (error) {
      setConfigError(error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "CONFIG_ERROR", message: "Could not load Natalify configuration.", retryable: true }, 0));
    }
  }, []);

  const loadOrders = useCallback(async () => {
    const token = ++refreshToken.current;
    setLoading(true);
    try {
      const response = await api.getOrders();
      if (token !== refreshToken.current) return;
      const preferredId = currentOrderIdRef.current;
      setOrders(response.orders);
      setAttention(response.attention);
      setLoadError(null);
      setStale(false);
      setCurrentIndex((previous) => {
        const preferredIndex = preferredId ? response.orders.findIndex((order) => order.id === preferredId) : -1;
        return preferredIndex >= 0 ? preferredIndex : Math.min(previous, Math.max(0, response.orders.length - 1));
      });
      if (preferredId && !response.orders.some((order) => order.id === preferredId) && screenRef.current === "orders") setActiveOrderId(null);
    } catch (error) {
      if (token !== refreshToken.current) return;
      const apiError = error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "ORDERS_ERROR", message: "Could not load Shopify orders. Retry the refresh.", retryable: true }, 0);
      setLoadError(apiError);
      if (ordersRef.current.length > 0) setStale(true);
    } finally {
      if (token === refreshToken.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([loadConfig(), loadOrders()]);
  }, [loadConfig, loadOrders]);

  useEffect(() => {
    const shouldWarn = screen === "picking" || screen === "delivery";
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarn) return;
      event.preventDefault();
      event.returnValue = "Current packing work will be lost.";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [screen]);

  const activeWork = screen !== "orders";
  const requestConfirmation = (confirmationRequest: PendingConfirmation) => setConfirmation(confirmationRequest);
  const dismissWrongScanToast = () => {
    wrongScanRequest.current += 1;
    setWrongScanToast(null);
  };
  const displayWrongScanToast = (next: WrongScanToast) => {
    setWrongScanToast(next);
  };
  const reportWrongScan = (scanned: string, expected: PickUnit | null) => {
    const requestId = ++wrongScanRequest.current;
    const expectedVariant = expected?.variant ?? null;
    displayWrongScanToast({ scanned, expected: expectedVariant, variants: [], status: "loading" });
    void api.lookupOnsiteVariant(scanned).then((lookup) => {
      if (requestId !== wrongScanRequest.current) return;
      const variants = lookup.status === "found" ? [lookup.variant] : lookup.status === "ambiguous" ? lookup.variants : [];
      displayWrongScanToast({ scanned, expected: expectedVariant, variants, status: lookup.status });
    }).catch(() => {
      if (requestId === wrongScanRequest.current) displayWrongScanToast({ scanned, expected: expectedVariant, variants: [], status: "unavailable" });
    });
  };
  const resetOrderSession = () => {
    dismissWrongScanToast();
    setActiveOrderId(null);
    setDelivery(null);
    setConfirmedIds(new Set());
    setCurrentUnitId(null);
    setScanFeedback(null);
    setAmbiguousCandidates(null);
    setManualDialogOpen(false);
  };
  const returnToOrders = () => {
    setScreen("orders");
    resetOrderSession();
  };

  const performRefresh = () => {
    returnToOrders();
    void loadOrders();
  };
  const refresh = () => {
    if (activeWork) {
      requestConfirmation({ title: "Refresh the order queue?", description: "Current packing work will be discarded.", confirmLabel: "Refresh orders", destructive: true, onConfirm: performRefresh });
      return;
    }
    performRefresh();
  };

  const beginPicking = () => {
    if (!selectedOrder || stale) return;
    dismissWrongScanToast();
    setActiveOrderId(selectedOrder.id);
    setConfirmedIds(new Set());
    setCurrentUnitId(selectedOrder.units[0]?.unitId ?? null);
    setScanFeedback(null);
    setAmbiguousCandidates(null);
    setManualDialogOpen(false);
    setScreen("picking");
  };

  const beginOnsiteSale = () => {
    setToast(null);
    setScreen("onsite");
  };

  const goBackToOrders = () => {
    requestConfirmation({ title: "Leave this order?", description: "Current packing work will be discarded.", confirmLabel: "Leave order", destructive: true, onConfirm: returnToOrders });
  };

  const goBackToPicking = () => {
    const returnToPicking = () => {
      setScreen("picking");
      setDelivery(null);
    };
    requestConfirmation({ title: "Return to picking?", description: "The current completion step will be discarded.", confirmLabel: "Return to picking", destructive: true, onConfirm: returnToPicking });
  };

  const confirmUnit = (unit: PickUnit, source: "scan" | "manual" | "resolution") => {
    const nextConfirmedIds = new Set(confirmedIds);
    nextConfirmedIds.add(unit.unitId);
    setConfirmedIds(nextConfirmedIds);
    const nextUnit = selectedOrder?.units.find((candidate) => !nextConfirmedIds.has(candidate.unitId));
    setCurrentUnitId(nextUnit?.unitId ?? null);
    setManualDialogOpen(false);
    setAmbiguousCandidates(null);
    if (source === "scan") setScanFeedback(null);
    if (source === "manual") setScanFeedback({ kind: "warning", title: "Item manually added", message: `${unit.variant.productName} was confirmed without barcode validation.` });
    if (source === "resolution") setScanFeedback(null);
    if (selectedOrder && nextConfirmedIds.size === selectedOrder.units.length) continueToDelivery(nextConfirmedIds);
  };

  const handleScan = (value: string) => {
    if (!selectedOrder || screen !== "picking") return;
    dismissWrongScanToast();
    const result: BarcodeMatch = matchBarcode(selectedOrder.units, confirmedIds, value);
    if (result.kind === "matched") {
      confirmUnit(result.unit, "scan");
    } else if (result.kind === "ambiguous") {
      setAmbiguousCandidates(result.candidates);
      setScanFeedback({ kind: "error", title: "Barcode needs a deliberate choice", message: "This code belongs to more than one distinct variant. Choose the exact item in the dialog." });
    } else if (result.kind === "duplicate") {
      setScanFeedback({ kind: "warning", title: "Already added — no extra item confirmed", message: "That code belongs to a unit already confirmed. Natalify did not mark another unrelated item.", scanned: result.scanned, context: `${result.confirmedUnit.variant.productName} · ${result.confirmedUnit.variant.color ?? "Color n/a"} · ${result.confirmedUnit.variant.size ?? "Size n/a"}` });
    } else if (result.reason === "no-match") {
      setScanFeedback(null);
      reportWrongScan(result.scanned, currentUnit ?? result.expected);
    } else {
      setScanFeedback({ kind: "error", title: "Barcode is not available", message: "The remaining item has no provider barcode. Use the deliberate manual confirmation route.", scanned: result.scanned, expected: "No barcode available for the remaining item" });
    }
  };

  const requestManualConfirmation = () => {
    if (currentUnit) setManualDialogOpen(true);
  };

  const undoUnit = (unitId: string) => {
    setConfirmedIds((previous) => {
      const next = new Set(previous);
      next.delete(unitId);
      return next;
    });
    setCurrentUnitId(unitId);
    setScanFeedback({ kind: "warning", title: "Item moved back to remaining", message: "Review it and confirm again with a scan or manual confirmation." });
  };

  function continueToDelivery(confirmedOverride?: ReadonlySet<string>) {
    const confirmedCount = confirmedOverride?.size ?? confirmedIds.size;
    if (!selectedOrder || confirmedCount !== selectedOrder.units.length || stale) return;
    setDelivery({
      fulfillmentOperationKey: newOperationKey(`fulfill-${selectedOrder.id}`),
      fulfillmentStatus: "idle",
      fulfillmentError: null,
      fulfillmentUnknown: false,
      fulfillmentReconciliationMessage: "",
    });
    setScreen("delivery");
  }

  const completeSuccessfully = async () => {
    setToast(`${selectedOrder?.orderNumber ?? "Order"} marked fulfilled in Shopify.`);
    returnToOrders();
    await loadOrders();
  };

  const fulfill = async () => {
    if (!delivery || !selectedOrder || !selectedOrder.units.length) return;
    setDelivery((previous) => previous ? { ...previous, fulfillmentStatus: "pending", fulfillmentError: null, fulfillmentUnknown: false } : previous);
    try {
      await api.fulfill({ orderId: selectedOrder.id, unitIds: [...confirmedIds], orderVersion: selectedOrder.version, operationKey: delivery.fulfillmentOperationKey });
      await completeSuccessfully();
    } catch (error) {
      const apiError = error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "FULFILLMENT_ERROR", message: "Could not complete the order.", retryable: true }, 0);
      setDelivery((previous) => previous ? { ...previous, fulfillmentStatus: "idle", fulfillmentError: apiError, fulfillmentUnknown: apiError.payload.category === "unknown" } : previous);
    }
  };

  const reconcileFulfillment = async () => {
    if (!delivery || !selectedOrder) return;
    setDelivery((previous) => previous ? { ...previous, fulfillmentStatus: "pending" } : previous);
    try {
      const response = await api.reconcileFulfillment(selectedOrder.id, delivery.fulfillmentOperationKey);
      if (response.status === "success") {
        await completeSuccessfully();
      } else {
        setDelivery((previous) => previous ? { ...previous, fulfillmentStatus: "idle", fulfillmentReconciliationMessage: `${response.message} Keep the order open and reconcile again before retrying.` } : previous);
      }
    } catch (error) {
      const apiError = error instanceof ApiError ? error : new ApiError({ category: "unavailable", code: "FULFILLMENT_RECONCILE_ERROR", message: "Could not reconcile fulfillment. Retry reconciliation.", retryable: true }, 0);
      setDelivery((previous) => previous ? { ...previous, fulfillmentStatus: "idle", fulfillmentError: apiError } : previous);
    }
  };

  const performScenarioChange = async (scenario: ScenarioName) => {
    setMockBusy(true);
    refreshToken.current += 1;
    try {
      await api.setScenario(scenario);
      returnToOrders();
      await Promise.all([loadConfig(), loadOrders()]);
    } catch (error) {
      setToast(error instanceof ApiError ? error.message : "Could not change the mock scenario.");
    } finally { setMockBusy(false); }
  };

  const changeScenario = (scenario: ScenarioName) => {
    if (activeWork) {
      requestConfirmation({ title: "Change the demo scenario?", description: "Current workflow work will be discarded and the selected scenario will be loaded.", confirmLabel: "Change scenario", destructive: true, onConfirm: () => performScenarioChange(scenario) });
      return;
    }
    void performScenarioChange(scenario);
  };

  const performScenarioReset = async () => {
    setMockBusy(true);
    refreshToken.current += 1;
    try {
      await api.resetScenario();
      returnToOrders();
      await Promise.all([loadConfig(), loadOrders()]);
    } catch (error) {
      setToast(error instanceof ApiError ? error.message : "Could not reset the mock scenario.");
    } finally { setMockBusy(false); }
  };

  const resetScenario = () => {
    if (activeWork) {
      requestConfirmation({ title: "Reset the demo scenario?", description: "Current workflow work will be discarded and the current scenario will return to its starting data.", confirmLabel: "Reset scenario", destructive: true, onConfirm: performScenarioReset });
      return;
    }
    void performScenarioReset();
  };

  const onPrevious = () => { setActiveOrderId(null); setCurrentIndex((index) => Math.max(0, index - 1)); };
  const onNext = () => { setActiveOrderId(null); setCurrentIndex((index) => Math.min(orders.length - 1, index + 1)); };
  const toggleTheme = () => setThemePreference((previous) => previous === "light" ? "dark" : "light");

  if (!config && configError) {
    return <div className="app-frame"><section className="card fatal-config"><p className="eyebrow">Natalify</p><h1>Configuration unavailable</h1><p>{configError.message}</p><button className="button button-primary" type="button" onClick={() => { void loadConfig(); void loadOrders(); }}>Retry</button></section></div>;
  }
  if (!config) return <div className="app-frame"><section className="card loading-card"><div className="spinner" /><h1>Starting Natalify…</h1><p className="muted">Connecting to the order service.</p></section></div>;

  return (
    <div className="app-frame">
      {screen === "onsite" ? <OnsiteFlow config={config} theme={theme} onToggleTheme={toggleTheme} onBackToOrders={() => setScreen("orders")} /> : <>
        <AppHeader screen={screen} theme={theme} mode={config.mode} refreshing={loading} onRefresh={refresh} onToggleTheme={toggleTheme} onNavigate={(target) => { if (target === "orders") goBackToOrders(); if (target === "picking") goBackToPicking(); }} />
        {screen === "orders" && <OrdersScreen orders={orders} currentIndex={currentIndex} loading={loading} loadError={loadError} stale={stale} attention={attention} adminUrl={config.shopifyAdminUrl} onRefresh={refresh} onPrevious={onPrevious} onNext={onNext} onView={beginPicking} onOnsiteSale={beginOnsiteSale} />}
        {screen === "picking" && selectedOrder && <PickingScreen order={selectedOrder} confirmedIds={confirmedIds} currentUnit={currentUnit} demoScanEnabled={config.mode === "mock"} feedback={scanFeedback} wrongScanToast={wrongScanToast} ambiguousCandidates={ambiguousCandidates} inputBlocked={manualDialogOpen} onDemoScan={() => { if (currentUnit?.variant.barcode) handleScan(currentUnit.variant.barcode); }} onScan={handleScan} onSelect={(unitId) => { if (!confirmedIds.has(unitId)) setCurrentUnitId(unitId); }} onUndo={undoUnit} onManualRequest={requestManualConfirmation} onResolveAmbiguous={(unitId) => { const unit = selectedOrder.units.find((candidate) => candidate.unitId === unitId); if (unit) confirmUnit(unit, "resolution"); }} onCloseAmbiguous={() => setAmbiguousCandidates(null)} onDismissWrongScan={dismissWrongScanToast} />}
        {screen === "delivery" && selectedOrder && delivery && <DeliveryScreen order={selectedOrder} confirmedCount={confirmedIds.size} delivery={delivery} readOnly={!config.shopifyFulfillmentWritesEnabled} onFulfill={fulfill} onReconcileFulfillment={reconcileFulfillment} />}
        {!selectedOrder && screen !== "orders" && <section className="card empty-card"><h1>Order no longer available</h1><p className="muted">Refresh the queue to reconstruct the current Shopify situation.</p><button className="button button-primary" type="button" onClick={refresh}>Return to orders</button></section>}
        {config.mode === "mock" && <MockTools config={config} busy={mockBusy} onScenarioChange={changeScenario} onReset={resetScenario} />}
      </>}
      {manualDialogOpen && currentUnit && <Dialog title="Confirm item manually?" description="This bypasses barcode validation for this specific physical unit." onClose={() => setManualDialogOpen(false)}><div className="manual-confirm-card"><ProductImage variant={currentUnit.variant} alt="" className="resolution-image" /><div><h3>{currentUnit.variant.productName}</h3><p className="variant-emphasis">{currentUnit.variant.color ?? "Color n/a"} · {currentUnit.variant.size ?? "Size n/a"}</p><p className="muted">SKU: <code>{currentUnit.variant.sku}</code></p></div></div><div className="dialog-actions"><button className="button button-quiet" type="button" onClick={() => setManualDialogOpen(false)}>Cancel</button><button className="button button-primary" type="button" onClick={() => confirmUnit(currentUnit, "manual")}>Yes, mark as added</button></div></Dialog>}
      {confirmation && <Dialog title={confirmation.title} description={confirmation.description} onClose={() => setConfirmation(null)}><div className="dialog-actions"><button className="button button-quiet" type="button" onClick={() => setConfirmation(null)}>Cancel</button><button className={`button ${confirmation.destructive ? "button-danger" : "button-primary"}`} type="button" onClick={() => { const action = confirmation.onConfirm; setConfirmation(null); void action(); }}>{confirmation.confirmLabel}</button></div></Dialog>}
      {toast && <CloseablePanel className="toast" role="status" resetKey={toast} closeLabel="Close notification" onClose={() => setToast(null)}>✓ {toast}</CloseablePanel>}
    </div>
  );
}
